import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { XCircle, RefreshCw, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dedicated post-payment failure page.
 * URL: /payments/failed/:paymentId
 * Shows the specific reason if we know it (cancelled vs failed), plus a
 * "Try again" button that returns the user to the packages page.
 */
export const PaymentFailedPage = () => {
  const { paymentId } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/payments/${paymentId}`, { withCredentials: true })
      .then((r) => setPayment(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load payment'));
  }, [paymentId]);

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  if (error) {
    return (
      <div className={`min-h-screen bg-gray-50 flex items-center justify-center p-4 ${dir}`} dir={dir}>
        <Card className="max-w-lg w-full p-8 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/subscriptions')}>
            {t('العودة إلى الباقات', 'Back to packages', 'Retour')}
          </Button>
        </Card>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className={`min-h-screen bg-gray-50 flex items-center justify-center ${dir}`} dir={dir}>
        <Loader2 className="animate-spin text-[#1e88e5]" size={40} />
      </div>
    );
  }

  const wasCancelled = payment.status === 'cancelled';
  const packageName = language === 'ar' ? (payment.package_name_ar || payment.package_name_en) : (payment.package_name_en || payment.package_name_ar);

  return (
    <div className={`min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4 ${dir}`} dir={dir} data-testid="payment-failed-page">
      <Card className="max-w-xl w-full p-8 md:p-10 shadow-xl border-2 border-red-100">
        <div className="text-center">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5 ${wasCancelled ? 'bg-orange-500' : 'bg-red-500'} text-white`}>
            {wasCancelled
              ? <AlertTriangle size={44} data-testid="failed-icon-cancelled" />
              : <XCircle size={44} data-testid="failed-icon-failed" />
            }
          </div>

          <h1 className="text-3xl font-bold text-[#2c3e50] mb-2" data-testid="failed-title">
            {wasCancelled
              ? t('تم إلغاء الدفع', 'Payment Cancelled', 'Paiement Annulé')
              : t('فشل الدفع', 'Payment Failed', 'Paiement Échoué')
            }
          </h1>

          <p className="text-gray-600 mb-8 max-w-md mx-auto" data-testid="failed-subtitle">
            {wasCancelled
              ? t('لم تكتمل عملية الدفع. لم يتم خصم أي مبلغ.',
                  'Your payment was cancelled. Nothing was charged.',
                  'Paiement annulé. Aucun montant débité.')
              : t('لم نتمكن من إكمال الدفع. الرجاء المحاولة مرة أخرى.',
                  'We could not complete your payment. Please try again.',
                  'Nous n\'avons pas pu finaliser le paiement.')
            }
          </p>

          <div className="bg-gray-50 rounded-2xl p-5 mb-8 text-start" data-testid="failed-details">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500">{t('الاستشارة', 'Consultation', 'Consultation')}</span>
              <span className="font-semibold text-[#2c3e50]" data-testid="failed-package">{packageName}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500">{t('المبلغ', 'Amount', 'Montant')}</span>
              <span className="font-bold text-gray-700 text-lg" data-testid="failed-amount">
                {payment.amount} {(payment.currency || 'USD').toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">{t('المرجع', 'Reference', 'Référence')}</span>
              <span className="font-mono text-xs text-gray-700" data-testid="failed-reference">{payment.reference}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-testid="failed-actions">
            <Button
              onClick={() => navigate('/subscriptions')}
              className="bg-[#1e88e5] hover:bg-[#1976d2] text-white h-12 px-6 rounded-xl text-base"
              data-testid="failed-cta-retry"
            >
              <RefreshCw size={18} className="me-2" />
              {t('حاول مرة أخرى', 'Try Again', 'Réessayer')}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/patient')}
              className="h-12 px-6 rounded-xl text-base"
              data-testid="failed-cta-back"
            >
              <ArrowLeft size={18} className="me-2" />
              {t('العودة إلى حسابي', 'Back to My Account', 'Retour')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PaymentFailedPage;
